<template>
  <basic-dialog :show="show" @close="$emit('close')" maxWidth="1000px">
    <template v-slot:title>Pricing Breakdown</template>

    <div v-if="!explainer" class="pa-4 text-center grey--text">
      No price breakdown available. Recalculate this service to generate one.
    </div>

    <template v-else>
      <div class="caption grey--text mb-2" v-if="explainer.recalculatedAt">
        Calculated {{ explainer.recalculatedAt | formatDate }}
        <span v-if="explainer.schemaVersion"
          >· schema v{{ explainer.schemaVersion }}</span
        >
      </div>

      <!-- Flags -->
      <div class="mb-4" v-if="explainer.flags">
        <div class="subtitle-2 mb-1">Flags</div>
        <v-chip
          v-for="flag in flagChips"
          :key="flag.label"
          small
          label
          class="mr-2 mb-2"
          :color="flag.on ? 'primary' : 'grey lighten-2'"
          :text-color="flag.on ? 'white' : 'grey darken-1'"
        >
          {{ flag.label }}{{ flag.value != null ? ": " + flag.value : "" }}
        </v-chip>
      </div>

      <!-- Service line -->
      <div class="mb-4" v-if="explainer.serviceLine">
        <div class="subtitle-2 mb-1">Service Line</div>
        <v-simple-table dense>
          <tbody>
            <tr>
              <td class="font-weight-medium">Rate type</td>
              <td>
                {{ explainer.serviceLine.rateTypeName || "—" }}
                <span v-if="explainer.serviceLine.rateType" class="grey--text"
                  >({{ explainer.serviceLine.rateType }})</span
                >
              </td>
              <td class="font-weight-medium">Duration</td>
              <td>{{ explainer.serviceLine.duration }}</td>
            </tr>
            <tr>
              <td class="font-weight-medium">Pax</td>
              <td>
                A {{ explainer.serviceLine.adults }} · C
                {{ explainer.serviceLine.children }} · I
                {{ explainer.serviceLine.infants }}
              </td>
              <td class="font-weight-medium">Qty</td>
              <td>{{ explainer.serviceLine.qty }}</td>
            </tr>
            <tr>
              <td class="font-weight-medium">Comps</td>
              <td>
                Supplier {{ explainer.serviceLine.supplierComps }} · Pass
                {{ explainer.serviceLine.passComps }}
              </td>
              <td class="font-weight-medium">Cost ROE</td>
              <td>
                {{
                  explainer.serviceLine.roeCost != null
                    ? explainer.serviceLine.roeCost
                    : "—"
                }}
              </td>
            </tr>
            <tr>
              <td class="font-weight-medium">Total Cost (Supplier)</td>
              <td>
                {{ explainer.serviceLine.totalSupplier | priceLabeler }}
                {{ explainer.serviceLine.currencySupplier }}
              </td>
              <td class="font-weight-medium">Sell ROE</td>
              <td>
                {{
                  explainer.serviceLine.roeSell != null
                    ? explainer.serviceLine.roeSell
                    : "—"
                }}
              </td>
            </tr>
            <tr>
              <td class="font-weight-medium">Total Cost (Base)</td>
              <td>
                {{ explainer.serviceLine.totalBase | priceLabeler }}
                {{ explainer.serviceLine.currencyBase }}
              </td>
              <td class="font-weight-medium">Total Sell (Agent)</td>
              <td>
                {{ explainer.serviceLine.totalAgent | priceLabeler }}
                {{ explainer.serviceLine.currencyAgent }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <template
          v-if="
            explainer.serviceLine.unitPrices &&
            explainer.serviceLine.unitPrices.length
          "
        >
          <div class="subtitle-2 mt-3 mb-1">Unit Prices</div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th class="text-left">Kind</th>
                <th class="text-right">
                  Cost Supplier ({{ explainer.serviceLine.currencySupplier }})
                </th>
                <th class="text-right">
                  Sell Agent ({{ explainer.serviceLine.currencyAgent }})
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(up, i) in explainer.serviceLine.unitPrices"
                :key="'up' + i"
              >
                <td class="text-capitalize">{{ up.kind }}</td>
                <td class="text-right">{{ up.supplier | priceLabeler }}</td>
                <td class="text-right">{{ up.agent | priceLabeler }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </template>
      </div>

      <!-- #11329 - Extra: how this extra's own price was arrived at. Only present
           when the explainer belongs to an extra, never on a service. -->
      <div class="mb-4" v-if="explainer.extraLine">
        <div class="subtitle-2 mb-1">Extra</div>
        <div v-if="explainer.extraLine.percentPrice">
          Priced at
          <b>{{ explainer.extraLine.percentPrice }}%</b> of the parent service's
          <b>{{ percentBasisLabel }}</b> price
          <span v-if="explainer.extraLine.parentUnitSell != null">
            ({{ explainer.extraLine.parentUnitSell | priceLabeler }} sell<span
              v-if="explainer.extraLine.parentUnitBuy != null"
            >
              / {{ explainer.extraLine.parentUnitBuy | priceLabeler }} cost</span
            >)</span
          ><span v-if="explainer.extraLine.percentDay">
            on {{ explainer.extraLine.percentDay | formatDate }}</span
          >.
          <div class="caption grey--text">
            Percentage extras follow the parent price, so no markup is applied to
            them.
          </div>
        </div>
        <div v-else-if="explainer.extraLine.autoPrice === false" class="grey--text">
          Custom priced — the cost and sell were entered by hand, so no rate card
          or markup was used.
        </div>
        <div v-else>Priced from the extra's own cost.</div>
        <div class="grey--text mt-1" v-if="extraFlagsLabel">
          {{ extraFlagsLabel }}
        </div>
      </div>

      <!-- Markups applied -->
      <div
        class="mb-4"
        v-if="explainer.markupApplied && explainer.markupApplied.length"
      >
        <div class="subtitle-2 mb-1">Markups Applied</div>
        <v-data-table
          dense
          :items="explainer.markupApplied"
          :headers="markupHeaders"
          hide-default-footer
          disable-pagination
          :expanded.sync="expandedMarkups"
          :item-key="markupKey"
          show-expand
        >
          <template v-slot:item.appliedPercent="{ item }">
            {{ item.appliedPercent }}%
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-3 grey lighten-4">
              <div v-if="item.markupRecord">
                <strong>Matched markup record #{{ item.markupRecord.id }}</strong>
                <div class="mt-1">
                  Department: {{ item.markupRecord.department || "—" }} · Group:
                  {{ item.markupRecord.groupName || "—" }} · Destination:
                  {{ item.markupRecord.destinationName || "—" }}
                </div>
                <div>
                  Tags: {{ item.markupRecord.tagsList || "—" }} · Percent:
                  {{ item.markupRecord.percent }}% · Historical only:
                  {{ item.markupRecord.historicalOnly ? "Yes" : "No" }}
                </div>
                <div
                  v-if="
                    item.markupRecord.useDateRange &&
                    (item.markupRecord.validFrom || item.markupRecord.validTo)
                  "
                >
                  Itinerary dates:
                  {{ item.markupRecord.validFrom | formatDate }} –
                  {{ item.markupRecord.validTo | formatDate }}
                </div>
                <div
                  v-if="
                    item.markupRecord.itiCreatedFrom ||
                    item.markupRecord.itiCreatedTo
                  "
                >
                  Itinerary Creation date:
                  {{ item.markupRecord.itiCreatedFrom | formatDate }} –
                  {{ item.markupRecord.itiCreatedTo | formatDate }}
                </div>
              </div>
              <div v-else class="grey--text">
                No markup record (fallback / non-table source).
              </div>
            </td>
          </template>
        </v-data-table>
      </div>

      <!-- Agent markup -->
      <div class="mb-4" v-if="explainer.agentMarkup">
        <div class="subtitle-2 mb-1">Agent Markup</div>
        <div>
          {{ explainer.agentMarkup.appliedPercent }}% ·
          {{ explainer.agentMarkup.valueApplied | priceLabeler }}
        </div>
      </div>

      <!-- Commission (saved itinerary only; null on live getPricing) -->
      <!-- #7197 (Round-4) - ordered Markup, Commission, Discount, then Circuit
           Discount per journal 57942 (A47876 item c). -->
      <div class="mb-4" v-if="explainer.commission">
        <div class="subtitle-2 mb-1">Commission</div>
        <div>
          {{ commissionSourceLabel(explainer.commission.source) }} ·
          {{ explainer.commission.percent }}% ·
          {{ explainer.commission.valueApplied | priceLabeler }}
          {{ explainer.commission.currency }}
          <span
            v-if="explainer.commission.commissionRecord"
            class="grey--text"
          >
            (record #{{ explainer.commission.commissionRecord.id }})
          </span>
        </div>
      </div>

      <!-- Supplier discount -->
      <div class="mb-4" v-if="explainer.supplierDiscount">
        <div class="subtitle-2 mb-1">Supplier Discount</div>
        <div v-if="explainer.supplierDiscount.applied">
          Cost: {{ explainer.supplierDiscount.valueCost | priceLabeler }} · Sell:
          {{ explainer.supplierDiscount.valueSell | priceLabeler }}
          {{ explainer.supplierDiscount.currency }}
          <!-- #11329 - same level of detail as the circuit discount below -->
          <div
            v-if="explainer.supplierDiscount.discountRecord"
            class="grey--text mt-1"
          >
            <div>
              {{ explainer.supplierDiscount.discountRecord.description || "—" }}
              <span v-if="discountTermsLabel"> · {{ discountTermsLabel }}</span>
              <span v-if="explainer.supplierDiscount.discountRecord.book">
                · Book min
                {{ explainer.supplierDiscount.discountRecord.book }}</span
              >
            </div>
            <div
              v-if="
                explainer.supplierDiscount.discountRecord.validFrom ||
                explainer.supplierDiscount.discountRecord.validTo
              "
            >
              Valid
              {{ explainer.supplierDiscount.discountRecord.validFrom | formatDate }}
              –
              {{ explainer.supplierDiscount.discountRecord.validTo | formatDate }}
            </div>
            <div v-if="explainer.supplierDiscount.discountRecord.bookBeforeDate">
              Book before
              {{
                explainer.supplierDiscount.discountRecord.bookBeforeDate
                  | formatDate
              }}
            </div>
            <div v-if="explainer.supplierDiscount.discountRecord.advanceDays">
              {{ explainer.supplierDiscount.discountRecord.advanceDays }} day<span
                v-if="explainer.supplierDiscount.discountRecord.advanceDays > 1"
                >s</span
              >
              in advance
            </div>
            <div>
              Agent Group:
              {{ explainer.supplierDiscount.discountRecord.groupName || "All" }}
            </div>
          </div>
        </div>
        <div v-else class="grey--text">No supplier discount applied.</div>
      </div>

      <!-- Circuit discount (saved itinerary only; null on live getPricing) -->
      <div class="mb-4" v-if="explainer.circuitDiscount">
        <div class="subtitle-2 mb-1">Circuit Discount</div>
        <div v-if="explainer.circuitDiscount.applied">
          Cost: {{ explainer.circuitDiscount.valueCost | priceLabeler }} · Sell:
          {{ explainer.circuitDiscount.valueSell | priceLabeler }}
          <div
            v-if="explainer.circuitDiscount.record"
            class="grey--text mt-1"
          >
            <div>
              {{ explainer.circuitDiscount.record.description || "—" }} ·
              <span
                v-if="explainer.circuitDiscount.record.percentCost != null"
                >{{ explainer.circuitDiscount.record.percentCost }}% cost /
              </span>
              {{ explainer.circuitDiscount.record.percent }}% sell
              <span v-if="circuitStayLabel"> · {{ circuitStayLabel }}</span>
              <span v-if="circuitSuppliersLabel">
                · {{ circuitSuppliersLabel }}</span
              >
            </div>
            <div
              v-if="
                explainer.circuitDiscount.record.validFrom ||
                explainer.circuitDiscount.record.validTo
              "
            >
              Valid {{ explainer.circuitDiscount.record.validFrom | formatDate }}
              – {{ explainer.circuitDiscount.record.validTo | formatDate }}
            </div>
            <div
              v-if="
                explainer.circuitDiscount.record.bookAfter ||
                explainer.circuitDiscount.record.bookBy
              "
            >
              Itinerary creation
              {{ explainer.circuitDiscount.record.bookAfter | formatDate }} –
              {{ explainer.circuitDiscount.record.bookBy | formatDate }}
            </div>
            <div>
              Agent Group:
              {{ explainer.circuitDiscount.record.groupName || "All" }}
            </div>
            <div v-if="explainer.circuitDiscount.candidateCount > 1">
              {{ explainer.circuitDiscount.candidateCount }} eligible (best
              applied)
            </div>
          </div>
        </div>
        <div
          v-else-if="explainer.circuitDiscount.record"
          class="grey--text"
        >
          Eligible ({{ explainer.circuitDiscount.record.description || "—" }} ·
          <span
            v-if="explainer.circuitDiscount.record.percentCost != null"
            >{{ explainer.circuitDiscount.record.percentCost }}% cost / </span
          >{{ explainer.circuitDiscount.record.percent }}% sell) but not applied
          — e.g. a custom-priced / locked service.
        </div>
        <div v-else class="grey--text">No circuit discount applied.</div>
      </div>

      <!-- Summary -->
      <div class="mb-2" v-if="explainer.summary">
        <div class="subtitle-2 mb-1">Summary</div>
        <v-simple-table dense>
          <tbody>
            <tr>
              <td class="font-weight-medium">Total Cost Supplier</td>
              <td>
                {{ explainer.summary.totalSupplier | priceLabeler }}
                {{ summaryCurrencySupplier }}
              </td>
              <td class="font-weight-medium">Markup total</td>
              <td>
                {{ explainer.summary.markupValueTotal | priceLabeler }}
                {{ summaryCurrencyBase }}
              </td>
            </tr>
            <tr>
              <td class="font-weight-medium">Total Sell Agent</td>
              <td>
                {{ explainer.summary.totalAgent | priceLabeler }}
                {{ summaryCurrencyAgent }}
              </td>
              <td class="font-weight-medium">Discount total</td>
              <td>
                {{ explainer.summary.discountValueTotal | priceLabeler }}
                {{ summaryCurrencyAgent }}
              </td>
            </tr>
            <tr>
              <td class="font-weight-medium">Total Sell base</td>
              <td>
                {{ explainer.summary.totalBase | priceLabeler }}
                {{ summaryCurrencyBase }}
              </td>
              <td class="font-weight-medium">Circuit discount (cost/sell)</td>
              <td>
                {{ explainer.summary.circuitDiscountCost | priceLabeler }}
                {{ summaryCurrencySupplier }} /
                {{ explainer.summary.circuitDiscountSell | priceLabeler }}
                {{ summaryCurrencyAgent }}
              </td>
            </tr>
            <template v-if="explainer.summary.netProfit != null">
              <tr>
                <td class="font-weight-medium">Net profit</td>
                <td>
                  {{ explainer.summary.netProfit | priceLabeler }}
                  {{ summaryCurrencyBase }}
                </td>
                <td class="font-weight-medium">Markup %</td>
                <td>{{ explainer.summary.markupPercent }}%</td>
              </tr>
              <tr>
                <td class="font-weight-medium">Margin %</td>
                <td>{{ explainer.summary.marginPercent }}%</td>
                <td></td>
                <td></td>
              </tr>
            </template>
          </tbody>
        </v-simple-table>
        <div class="caption grey--text mt-1">
          Net profit is the markup less agent commission. Any circuit discount is
          shown on its own line and is not taken off the net profit.
        </div>
        <div class="caption grey--text mt-1">
          Circuit discounts are only applied after the itinerary is saved, once
          all conditions are met
        </div>
      </div>
    </template>
  </basic-dialog>
</template>

<script>
import BasicDialog from "../../../shared/BasicDialog.vue";

const COMMISSION_SOURCE_LABELS = {
  COMMISSION_TABLE: "Commission table",
  FINANCE_SETTING: "Finance setting",
  PACKAGE_COMMISSION: "Package commission",
  NONE: "None",
};

export default {
  name: "pricing-explainer-dialog",

  components: { BasicDialog },

  props: {
    explainer: {
      type: Object,
      default: () => null,
    },
    show: {
      type: Boolean,
      default: () => false,
    },
  },

  data() {
    return {
      expandedMarkups: [],
      markupHeaders: [
        { text: "Source", value: "source", sortable: false },
        {
          text: "Applied %",
          value: "appliedPercent",
          sortable: false,
          align: "end",
        },
        {
          text: "Candidates",
          value: "candidateCount",
          sortable: false,
          align: "end",
        },
        { text: "", value: "data-table-expand" },
      ],
    };
  },

  computed: {
    // #7197 - the summary node carries no currency of its own; reuse the
    // service-line currencies so each Summary amount can show its currency code.
    summaryCurrencyBase() {
      const sl = this.explainer && this.explainer.serviceLine;
      return sl && sl.currencyBase ? sl.currencyBase : "";
    },
    summaryCurrencySupplier() {
      const sl = this.explainer && this.explainer.serviceLine;
      return sl && sl.currencySupplier ? sl.currencySupplier : "";
    },
    summaryCurrencyAgent() {
      const sl = this.explainer && this.explainer.serviceLine;
      return sl && sl.currencyAgent ? sl.currencyAgent : "";
    },
    flagChips() {
      const f = this.explainer && this.explainer.flags;
      if (!f) {
        return [];
      }
      return [
        { label: "Skip Markup", on: f.skipMarkup },
        { label: "Skip Circuit Discount", on: f.skipCircuitDiscount },
        { label: "Auto Price", on: f.autoPrice },
        { label: "Historical Rates", on: f.useHistoricalRates },
        {
          label: "Year Shift",
          on: f.yearShift != null && f.yearShift != 0,
          value: f.yearShift,
        },
      ];
    },
    // "Stay 7–100 nights" style label from the selected circuit discount record
    circuitStayLabel() {
      const r =
        this.explainer &&
        this.explainer.circuitDiscount &&
        this.explainer.circuitDiscount.record;
      if (!r || (r.minStay == null && r.maxStay == null)) {
        return "";
      }
      return "Stay " + (r.minStay || 0) + "–" + (r.maxStay || "∞") + " nights";
    },
    // #11329 - which parent price row a percentage extra was taken from. The
    // backend emits the raw node name (item / adult / child); spell it out.
    percentBasisLabel() {
      const basis =
        this.explainer &&
        this.explainer.extraLine &&
        this.explainer.extraLine.percentBasis;
      return { item: "item", adult: "adult", child: "child" }[basis] || "adult";
    },
    // #11329 - the extra's own flags, as a single readable line
    extraFlagsLabel() {
      const e = this.explainer && this.explainer.extraLine;
      if (!e) {
        return "";
      }
      const parts = [];
      if (e.isChildOnly) {
        parts.push("Child only");
      }
      if (e.isInfantOnly) {
        parts.push("Infant only");
      }
      if (e.isMandatory) {
        parts.push("Mandatory");
      }
      if (e.optional) {
        parts.push("Optional");
      }
      if (e.included === false) {
        parts.push("Not included");
      }
      if (e.rateBase) {
        parts.push(e.rateBase == "I" ? "Per item" : "Per person");
      }
      if (e.pax) {
        parts.push("Pax " + e.pax);
      }
      return parts.join(" · ");
    },
    // #11329 - how the supplier discount actually works: a percentage, or free
    // nights/items, or both. Built as a label because a "book 3 get 1 free"
    // discount has percent 0, and printing a bare "0%" reads as a bug.
    discountTermsLabel() {
      const r =
        this.explainer &&
        this.explainer.supplierDiscount &&
        this.explainer.supplierDiscount.discountRecord;
      if (!r) {
        return "";
      }
      const parts = [];
      if (Number(r.percent) > 0) {
        parts.push(r.percent + "%");
      }
      if (Number(r.free) > 0) {
        parts.push(r.free + " free");
      }
      return parts.join(" · ");
    },
    // "Suppliers 1–2 max" style label from the selected circuit discount record
    circuitSuppliersLabel() {
      const r =
        this.explainer &&
        this.explainer.circuitDiscount &&
        this.explainer.circuitDiscount.record;
      if (!r || (r.minSuppliers == null && r.maxSuppliers == null)) {
        return "";
      }
      return (
        "Suppliers " + (r.minSuppliers || 0) + "–" + (r.maxSuppliers || "∞") + " max"
      );
    },
  },

  methods: {
    // v-data-table needs a stable key per row; markupApplied rows have no id.
    markupKey(item) {
      return [item.source, item.appliedPercent, item.dayFrom, item.dayTo].join(
        "|"
      );
    },
    commissionSourceLabel(source) {
      return COMMISSION_SOURCE_LABELS[source] != null
        ? COMMISSION_SOURCE_LABELS[source]
        : source;
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/builder/tools/PricingExplainerDialog.vue